import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer, useImmerReducer } from "use-immer";

import { IgButton, IgChip, IgTypography } from "@intelligems/ig-theme";
import { Badge, Grid, SvgIcon } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useGetOrg } from "../../../../api/organizations/get-organization";
import {
  AnalyticsFilterReducer,
  AnalyticsFilterReducerAction,
} from "../../../../api/reducers/analyticsFilterReducer";
import FilterButtonSVG from "../../../../components/DashboardFilters/FilterButtonSVG";
import { isDialogOpen, setDialog } from "../../../../store/slices/global";
import { updateSitewideDashboardFilter } from "../../../../store/slices/sitewideDashboardFilters";
import { AnalyticsActiveFilterType } from "../../../../store/types/dashboardFilters";
import {
  initialSitewideDashboardFiltersState,
  ISitewideDashboardFilters,
  SitewideDashboardFiltersDialog,
} from "../../../../store/types/sitewideDashboardFilters";
import { IAppState } from "../../../../store/types/store";
import theme from "../../../../theme";
import { fullDateTimeConverter } from "../../../../utils/dateConverters";
import { TimezoneLastUpdatedInfo } from "../../Utils/TimezoneLastUpdatedInfo";

import { AggregationSelect } from "./AggregationSelect";
import { DateRangePicker } from "./DateRangePicker";
import { SitewideFiltersDialog } from "./SitewideFiltersDialog";
const PREFIX = "SitewideDashboardHeader";

const classes = {
  filterButtons: `${PREFIX}-filterButtons`,
  text: `${PREFIX}-text`,
  flexRow: `${PREFIX}-flexRow`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.filterButtons}`]: {
    borderRadius: 15,
    textTransform: "none",
  },

  [`& .${classes.text}`]: {
    fontSize: "22px",
    fontWeight: 600,
    letterSpacing: ".50px",
  },

  [`& .${classes.flexRow}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}));
interface SitewideDashboardHeaderProps {
  latestRunTs: number | undefined;
}
export const SitewideDashboardHeader = ({
  latestRunTs,
}: SitewideDashboardHeaderProps) => {
  const dispatch = useDispatch();
  const organization = useGetOrg();
  const sitewideDashboardFilters = useSelector(
    (state: IAppState) => state.sitewideDashboardFilters
  );
  const isFiltersOpen = useSelector(isDialogOpen("dashboardFilters"));

  const [activeFilterArray, dispatchActiveFilterArray] = useImmerReducer<
    AnalyticsActiveFilterType[],
    AnalyticsFilterReducerAction
  >(AnalyticsFilterReducer, []);

  const [dialogFilters, setDialogFilters] =
    useImmer<SitewideDashboardFiltersDialog>({
      ...initialSitewideDashboardFiltersState,
      countryCodes: [],
      subscriptionType: sitewideDashboardFilters.subscriptionType,
      deviceType: sitewideDashboardFilters.deviceType,
      visitorType: sitewideDashboardFilters.visitorType,
      channels: sitewideDashboardFilters.channels,
      urlParam: sitewideDashboardFilters.urlParam,
    });

  const handleOpen = () => {
    dispatch(setDialog({ dialog: "dashboardFilters", open: true }));
  };

  const onFilterChipDelete = (key: keyof ISitewideDashboardFilters) => {
    setDialogFilters((draft) => {
      draft[key as string] = initialSitewideDashboardFiltersState[key];

      return draft;
    });

    dispatch(
      updateSitewideDashboardFilter({
        key,
        value: initialSitewideDashboardFiltersState[key],
      })
    );
  };

  useEffect(() => {
    dispatchActiveFilterArray({
      type: "updateSitewideDeviceType",
      payload: { sitewideDashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateSitewideVisitorType",
      payload: { sitewideDashboardFilters },
    });

    dispatchActiveFilterArray({
      type: "updateSitewideCountryCodes",
      payload: { sitewideDashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateSitewideChannelType",
      payload: { sitewideDashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateSitewideUrlParam",
      payload: { sitewideDashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateSitewideSubscriptionType",
      payload: { sitewideDashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateSitewideChannelType",
      payload: { sitewideDashboardFilters },
    });
    dispatchActiveFilterArray({
      type: "updateSitewideUrlParam",
      payload: { sitewideDashboardFilters },
    });
  }, [sitewideDashboardFilters]);

  return (
    <StyledGrid item container direction={"column"}>
      <IgTypography className={classes.text}>
        Sitewide Analytics Dashboard
      </IgTypography>
      <Grid
        item
        container
        direction={"column"}
        rowSpacing={1}
        columnSpacing={1}
      >
        <TimezoneLastUpdatedInfo
          showLastUpdatedTime={true}
          latestRunTs={latestRunTs}
        />
        <Grid
          container
          item
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Grid item xs={6} container direction={"row"} columnSpacing={1}>
            <Grid item>
              <DateRangePicker />
            </Grid>
            <Grid item>
              <AggregationSelect />
            </Grid>
          </Grid>
          <Grid
            xs={6}
            columnSpacing={1}
            item
            container
            direction={"row"}
            justifyContent={"flex-end"}
          >
            <Grid item>
              <Badge color={"primary"} badgeContent={activeFilterArray.length}>
                <IgButton
                  variant={"outlined"}
                  sx={{
                    color: theme.palette.gray.main,
                    border: `1px solid ${
                      activeFilterArray.length
                        ? theme.palette.primary.main
                        : theme.palette.disabled.main
                    }`,
                    backgroundColor: "white",
                  }}
                  startIcon={
                    <SvgIcon
                      viewBox="0 0 512 357"
                      component={FilterButtonSVG}
                    />
                  }
                  onClick={handleOpen}
                >
                  Filters
                </IgButton>
              </Badge>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          container
          direction={"row"}
          alignItems={"center"}
          columnSpacing={1}
          justifyContent={"flex-end"}
        >
          {activeFilterArray.map((filter) => (
            <Grid item key={filter.key}>
              <IgChip
                onDelete={() => {
                  onFilterChipDelete(
                    filter.key as keyof ISitewideDashboardFilters
                  );
                }}
                variant={"filled"}
                label={filter.label}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <SitewideFiltersDialog
        openFilters={isFiltersOpen}
        dialogFilters={dialogFilters}
        setDialogFilters={setDialogFilters}
      />
    </StyledGrid>
  );
};
